import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecretaryStore } from '@/modules/secretary/store/secretaryStore';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { Calendar, Users, Activity, LogOut } from 'lucide-react';
import styles from './SecretaryLayout.module.css';
import { WorkspaceSwitcher } from '@/modules/secretary/components/WorkspaceSwitcher';

// Obtiene las iniciales del nombre para el avatar
function getInitials(name: string | null | undefined): string {
  if (!name) return 'S';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0].toUpperCase())
    .join('');
}

export default function SecretaryLayout({ children }: Readonly<{ children: React.ReactNode }>) {
  const { activeWorkspaceId, activeWorkspaceType, setActiveWorkspace } = useSecretaryStore();
  const { profile } = useProfileStore();
  const navigate = useNavigate();
  const [navActive, setNavActive] = useState<'agenda' | 'directorio'>('agenda');
  const [workspaces, setWorkspaces] = useState<{ id: string, name: string }[]>([]);
  const [equipos, setEquipos] = useState<{ id: string, name: string }[]>([]);

  React.useEffect(() => {
    if (!profile?.perfil_usuario_id) return;
    const fetchContexts = async () => {
      // 1. Fetch Personal Workspaces (Escenario 1)
      const { data: dataW } = await supabase
        .from('workspace_miembros')
        .select(`negocio_uuid, unidades_negocio(vendedor_uuid, vendedor(perfiles_usuario(nombres_completos, apellidos_completos, avatar_url)))`)
        .eq('miembro_uuid', profile.perfil_usuario_id)
        .eq('rol_workspace', 'secretaria')
        .eq('estado', 'activo');
      
      let mappedW: { id: string, name: string }[] = [];
      if (dataW) {
        mappedW = dataW.map((w: any) => {
          const v = w.unidades_negocio?.vendedor;
          const vData = Array.isArray(v) ? v[0] : v;
          const p = vData?.perfiles_usuario;
          const pData = Array.isArray(p) ? p[0] : p;
          const name = pData ? `${pData.nombres_completos || ''} ${pData.apellidos_completos || ''}`.trim() : `Agenda ···${w.negocio_uuid.slice(-4).toUpperCase()}`;
          return {
            id: w.negocio_uuid,
            name,
            avatar: pData?.avatar_url || null
          };
        });
        setWorkspaces(mappedW);
      }

      // 2. Fetch Equipos Clínicos (Escenario 2)
      const { data: dataE } = await supabase
        .from('equipo_miembros')
        .select(`equipo_uuid, equipos_clinicos(nombre_display)`)
        .eq('miembro_uuid', profile.perfil_usuario_id)
        .eq('rol_equipo', 'secretaria'); // La secretaria gestiona agendas

      let mappedE: { id: string, name: string }[] = [];
      if (dataE) {
        mappedE = dataE.map((e: any) => ({
          id: e.equipo_uuid,
          name: e.equipos_clinicos?.nombre_display || `Equipo ···${e.equipo_uuid.slice(-4).toUpperCase()}`
        }));
        setEquipos(mappedE);
      }
        
      // Auto-seleccionar el primero disponible si no hay uno activo
      if (!useSecretaryStore.getState().activeWorkspaceId) {
        if (mappedW.length > 0) setActiveWorkspace(mappedW[0].id, 'personal');
        else if (mappedE.length > 0) setActiveWorkspace(mappedE[0].id, 'equipo');
      }
    };
    fetchContexts();
  }, [profile?.perfil_usuario_id, setActiveWorkspace]);

  const initials = getInitials(profile?.nombres_completos);
  const firstName = profile?.nombres_completos?.split(' ')[0] ?? 'Secretaria';

  const handleSalir = async () => {
    await supabase.auth.signOut();
    navigate('/hub');
  };

  const handleAbandon = async (id: string, type: 'personal' | 'equipo') => {
    const label = type === 'personal' ? 'esta agenda' : 'este equipo clínico';
    if (!globalThis.confirm(`¿Estás seguro de que deseas abandonar ${label}? Esta acción no se puede deshacer.`)) return;

    try {
      let result;
      if (type === 'personal') {
        result = await supabase.rpc('fn_abandonar_workspace', { p_negocio_uuid: id });
      } else {
        result = await supabase.rpc('fn_abandonar_equipo', { p_equipo_uuid: id });
      }

      if (result.error) throw result.error;
      if (result.data && !result.data.success) throw new Error(result.data.error);

      if (type === 'personal') {
        setWorkspaces(prev => prev.filter(w => w.id !== id));
      } else {
        setEquipos(prev => prev.filter(e => e.id !== id));
      }

      if (useSecretaryStore.getState().activeWorkspaceId === id) {
        const remainingW = workspaces.filter(w => w.id !== id);
        const remainingE = equipos.filter(e => e.id !== id);
        if (remainingW.length > 0) setActiveWorkspace(remainingW[0].id, 'personal');
        else if (remainingE.length > 0) setActiveWorkspace(remainingE[0].id, 'equipo');
        else setActiveWorkspace(null as any, 'personal');
      }
    } catch (error: any) {
      globalThis.alert('No se pudo abandonar: ' + error.message);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-dvh w-full bg-slate-50 overflow-hidden">
      
      {/* ============================================
          SIDEBAR (ESCRITORIO / TABLET)
          ============================================ */}
      <aside className={`hidden md:flex flex-col w-[280px] shrink-0 bg-white border-r border-slate-200 pt-8`}>
        {/* Header del Sidebar */}
        <div className="flex items-center gap-3 px-6 pb-6 border-b border-slate-200">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white bg-linear-to-br from-[#0052CC] to-[#1D4ED8]">
            <Activity size={16} color="white" />
          </div>
          <div>
            <p className="text-base font-extrabold text-[#0A2540] tracking-tight">Aura CRM</p>
            <p className="text-[10px] font-semibold text-[#0052CC] uppercase tracking-wider">Panel Logístico</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 px-4 mt-6 flex-1">
          <button
            className={`${styles.navItem} ${navActive === 'agenda' ? styles.navItemActive : ''}`}
            onClick={() => setNavActive('agenda')}
          >
            <Calendar size={20} className="shrink-0" />
            <span className="text-[15px]">Agenda Hub</span>
          </button>
          <button
            className={`${styles.navItem} ${navActive === 'directorio' ? styles.navItemActive : ''}`}
            onClick={() => setNavActive('directorio')}
          >
            <Users size={20} className="shrink-0" />
            <span className="text-[15px]">Directorio Clínico</span>
          </button>
        </nav>

        {/* Footer con perfil y salir */}
        <div className="flex flex-col gap-3 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
            <div className="w-9 h-9 rounded-full bg-linear-to-br from-[#0052CC] to-[#1D4ED8] text-white flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden shadow-sm">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={firstName} className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="overflow-hidden">
              <p className="text-[13px] font-bold text-[#0A2540] truncate">{firstName}</p>
              <p className="text-[11px] font-semibold text-[#0052CC]">Secretaria</p>
            </div>
          </div>
          <button className={styles.btnHub} onClick={() => navigate('/hub')}>
            <Activity size={16} className="shrink-0" />
            <span>Volver al Hub</span>
          </button>
          <button className={styles.btnSalir} onClick={handleSalir}>
            <LogOut size={16} className="shrink-0" />
            <span>Salir</span>
          </button>
        </div>
      </aside>

      {/* ============================================
          MAIN CONTENT AREA
          ============================================ */}
      <main className="flex-1 flex flex-col overflow-y-auto">
        {/* Top Header */}
        <header className="h-[56px] min-h-[56px] border-b border-slate-200 bg-white flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center">
            <WorkspaceSwitcher
              workspaces={workspaces}
              equipos={equipos}
              activeId={activeWorkspaceId}
              activeType={activeWorkspaceType || 'personal'}
              onChange={(id, type) => setActiveWorkspace(id, type)}
              onAbandon={handleAbandon}
            />
          </div>
        </header>

        {/* Dynamic Content */}
        <div className="flex-1 w-full max-w-7xl mx-auto p-6 md:p-8 flex flex-col gap-6 md:gap-8 overflow-auto">
          {children}
        </div>
      </main>

      {/* ============================================
          BOTTOM TAB BAR (MÓVIL)
          ============================================ */}
      <nav className="md:hidden flex flex-row border-t border-slate-200 h-[76px] shrink-0 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <button
          className="flex-1 flex flex-col justify-center items-center bg-transparent border-none py-2 cursor-pointer"
          onClick={() => setNavActive('agenda')}
        >
          <Calendar size={28} color={navActive === 'agenda' ? '#1D4ED8' : '#64748B'} />
          <span className={`text-xs mt-1 ${navActive === 'agenda' ? 'font-bold text-[#1D4ED8]' : 'font-normal text-[#64748B]'}`}>
            Agenda
          </span>
        </button>
        <button
          className="flex-1 flex flex-col justify-center items-center bg-transparent border-none py-2 cursor-pointer"
          onClick={() => setNavActive('directorio')}
        >
          <Users size={28} color={navActive === 'directorio' ? '#1D4ED8' : '#64748B'} />
          <span className={`text-xs mt-1 ${navActive === 'directorio' ? 'font-bold text-[#1D4ED8]' : 'font-normal text-[#64748B]'}`}>
            Directorio
          </span>
        </button>
        <button
          className="flex-1 flex flex-col justify-center items-center bg-transparent border-none py-2 cursor-pointer"
          onClick={() => navigate('/hub')}
        >
          <Activity size={28} color="#64748B" />
          <span className="text-xs mt-1 font-normal text-[#64748B]">Hub</span>
        </button>
      </nav>
    </div>
  );
}
